// Un bloque que se carga aparte (`getBlock`): su esqueleto mientras llega, su
// propio aviso si falla (el resto de la página sigue) y, al cambiar lo
// elegido, lo anterior atenuado hasta que llegan los datos nuevos. Cada
// bloque es su propia consulta: un cambio en sus registros solo lo vuelve a
// leer a él.

import { Component, useEffect, useRef, type ReactNode } from "react";
import { useQuery } from "convex/react";
import { AlertTriangle } from "lucide-react";
import { api } from "@/convex/_generated/api";
import type { SiteBlockResult } from "@/convex/sites/public";
import { cn } from "@/lib/utils";
import { BlockSkeleton } from "./blocks/block-skeleton";
import { SiteBlock, type SiteBlockActions } from "./blocks/site-blocks";
import type { DeferredBlock, ResolvedBlock } from "./values";

export interface DeferredSiteBlockProps {
  slug: string;
  token: string;
  page: string;
  placeholder: DeferredBlock;
  picks: Record<string, string>;
  /** La sesión venció (el servidor dijo `signed_out`). */
  onExpired: () => void;
  /** Acciones de los bloques (hoy ninguno de los que se cargan aparte las usa). */
  actions: SiteBlockActions;
}

export function DeferredSiteBlock(props: DeferredSiteBlockProps) {
  return (
    <BlockErrorBoundary resetKey={`${props.page}|${props.placeholder.key}|${JSON.stringify(props.picks)}`}>
      <DeferredContent {...props} />
    </BlockErrorBoundary>
  );
}

function DeferredContent({ slug, token, page, placeholder, picks, onExpired, actions }: DeferredSiteBlockProps) {
  const result = useQuery(api.sites.public.getBlock, {
    slug,
    token,
    page,
    block: placeholder.key,
    ...(Object.keys(picks).length ? { picks } : {}),
  }) as SiteBlockResult | undefined;

  // Mientras llega con otra elección, se deja lo anterior (atenuado).
  const last = useRef<{ page: string; block: ResolvedBlock } | null>(null);
  if (result?.status === "ok") last.current = { page, block: result.block };
  const shown =
    result?.status === "ok"
      ? result.block
      : result === undefined && last.current?.page === page && last.current.block.key === placeholder.key
        ? last.current.block
        : null;
  const refreshing = result === undefined && !!shown;

  useEffect(() => {
    if (result?.status === "signed_out") onExpired();
  }, [result?.status, onExpired]);

  // El bloque ya no existe (se editó el sitio): la página se actualiza sola.
  if (result?.status === "not_found" || result?.status === "signed_out") return null;
  if (!shown) return <BlockSkeleton block={placeholder} />;
  return (
    <div
      data-testid="site-deferred-block"
      data-block={placeholder.key}
      className={cn("transition-opacity", refreshing && "pointer-events-none opacity-60")}
      aria-busy={refreshing}
    >
      <SiteBlock block={shown} {...actions} />
    </div>
  );
}

/** Aviso de un bloque que no se pudo cargar (el resto de la página sigue). */
export function BlockError() {
  return (
    <div
      role="alert"
      data-testid="site-block-error"
      className="flex items-start gap-3 rounded-lg border border-border bg-card p-4 text-sm"
    >
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
      <div className="min-w-0 space-y-1">
        <p className="font-medium text-foreground">No pudimos cargar esta sección</p>
        <p className="text-muted-foreground">Puede ser un problema de conexión. Recarga la página para intentarlo de nuevo.</p>
      </div>
    </div>
  );
}

/** Errores de un bloque: su aviso, sin romper la página. Otra elección vuelve a intentar. */
class BlockErrorBoundary extends Component<{ resetKey: string; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidUpdate(prev: { resetKey: string }) {
    if (this.state.failed && prev.resetKey !== this.props.resetKey) this.setState({ failed: false });
  }
  render() {
    return this.state.failed ? <BlockError /> : this.props.children;
  }
}
